import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { clientPortalService } from '../../services/clientPortalService';
import RecommendationCard from './components/RecommendationCard';
import ImplementationRoadmap from './components/ImplementationRoadmap';
import ImpactAnalysis from './components/ImpactAnalysis';
import FilterPanel from './components/FilterPanel';

const PersonalizedActionCenter = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    priority: 'all',
    impact: 'all',
    complexity: 'all'
  });
  const [viewMode, setViewMode] = useState('grid'); // grid or roadmap

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    loadRecommendations();
  }, [user, navigate]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService?.getClientRecommendations(user?.id);
      setRecommendations(data || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadRecommendations();
  };

  const handleRecommendationSelect = (recommendationId) => {
    setSelectedRecommendations(prev => {
      if (prev?.includes(recommendationId)) {
        return prev?.filter(id => id !== recommendationId);
      }
      return [...prev, recommendationId];
    });
  };

  const handleStatusUpdate = async (recommendationId, newStatus) => {
    try {
      await clientPortalService?.updateRecommendationStatus(recommendationId, newStatus);
      await loadRecommendations();
    } catch (error) {
      console.error('Error updating recommendation status:', error);
      alert('Failed to update recommendation status');
    }
  };

  const filteredRecommendations = recommendations?.filter(rec => {
    if (filters?.category !== 'all' && rec?.category !== filters?.category) return false;
    if (filters?.priority !== 'all') {
      const priorityMap = { high: 1, medium: 2, low: 3 };
      if (priorityMap?.[filters?.priority] !== rec?.priority) return false;
    }
    if (filters?.impact !== 'all' && rec?.estimatedImpact?.toLowerCase()?.includes(filters?.impact) === false) return false;
    return true;
  });

  const activeRecommendations = filteredRecommendations?.filter(r => r?.status === 'active');
  const completedRecommendations = filteredRecommendations?.filter(r => r?.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-[#1FB5A5] mx-auto" />
            <p className="mt-4 text-white">Loading your personalized recommendations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-400">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Personalized Action Center | Northstar Data Strategy</title>
        <meta
          name="description"
          content="Strategic recommendations tailored to your organization's data maturity journey with actionable implementation guidance."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-[#1FB5A5]/20 to-[#002B45]/20 backdrop-blur-sm rounded-xl p-8 border border-[#1FB5A5]/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                      Personalized Action Center
                    </h1>
                    <p className="text-gray-300 max-w-3xl">
                      Strategic recommendations powered by your assessment data and consultation insights. Each recommendation includes detailed implementation guidance, ROI projections, and resource requirements tailored to your organization's unique context.
                    </p>
                  </div>
                  <Button
                    onClick={handleRefresh}
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  >
                    Refresh
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Active Recommendations</p>
                    <p className="text-3xl font-bold text-white">{activeRecommendations?.length}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Completed</p>
                    <p className="text-3xl font-bold text-[#1FB5A5]">{completedRecommendations?.length}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">In Roadmap</p>
                    <p className="text-3xl font-bold text-white">{selectedRecommendations?.length}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Total Recommendations</p>
                    <p className="text-3xl font-bold text-white">{recommendations?.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="mb-6">
              <div className="bg-white rounded-xl shadow-elevation-1 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="LayoutGrid" size={18} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">View Mode:</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                        viewMode === 'grid' ?'bg-[#1FB5A5] text-white shadow-md' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Icon name="Grid" size={16} className="inline mr-2" />
                      Grid View
                    </button>
                    <button
                      onClick={() => setViewMode('roadmap')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                        viewMode === 'roadmap' ?'bg-[#1FB5A5] text-white shadow-md' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Icon name="Map" size={16} className="inline mr-2" />
                      Roadmap View
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            <FilterPanel filters={filters} onFilterChange={setFilters} />

            {/* Impact Analysis */}
            {selectedRecommendations?.length > 0 && (
              <ImpactAnalysis
                recommendations={recommendations?.filter(r => selectedRecommendations?.includes(r?.id))}
              />
            )}

            {/* Content Area */}
            {viewMode === 'grid' ? (
              <div className="space-y-6">
                {/* Active Recommendations */}
                {activeRecommendations?.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Icon name="Lightbulb" size={24} className="text-[#1FB5A5]" />
                      Priority Recommendations
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {activeRecommendations?.map((recommendation) => (
                        <RecommendationCard
                          key={recommendation?.id}
                          recommendation={recommendation}
                          isSelected={selectedRecommendations?.includes(recommendation?.id)}
                          onSelect={handleRecommendationSelect}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed Recommendations */}
                {completedRecommendations?.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Icon name="CheckCircle" size={24} className="text-green-400" />
                      Completed Actions
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {completedRecommendations?.map((recommendation) => (
                        <RecommendationCard
                          key={recommendation?.id}
                          recommendation={recommendation}
                          isSelected={false}
                          onSelect={handleRecommendationSelect}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {filteredRecommendations?.length === 0 && (
                  <div className="bg-white rounded-xl shadow-elevation-1 p-12 text-center">
                    <Icon name="Lightbulb" size={48} className="text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No recommendations match your current filters</p>
                    <Button
                      onClick={() => setFilters({ category: 'all', priority: 'all', impact: 'all', complexity: 'all' })}
                      className="mt-4"
                      variant="outline"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <ImplementationRoadmap
                recommendations={recommendations?.filter(r => selectedRecommendations?.includes(r?.id))}
                allRecommendations={recommendations}
                onRecommendationSelect={handleRecommendationSelect}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalizedActionCenter;